import { useRef } from 'react';
import { FormField } from 'ui/formField';
import { WebPushContentDefinition } from 'modules/graphql/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { TextAreaField } from '../../common/components';
import { constructFieldErrors } from '../../common/utils';
import { ContentType, FieldNames, MediumDataKey } from '../../common/types';
import { WebPushRestrictionText } from '../../common/RestrictionText';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { constructErrorInlineText } from '../../common/ErrorInlineText';

const MAX_CHARACTERS = 240;
export const WEB_PUSH_BODY: MediumDataKey = 'message';
const displayContent = {
  labelText: 'Body',
};

export const showCharactersLeft = (textContent: string) =>
  `${textContent.length} / ${MAX_CHARACTERS} characters`;

export const WebPushBodyField = () => {
  const { liveTemplate, setCurrentMediumField, isEditMode } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const webPushData = (liveTemplate?.mediumData ?? {}) as WebPushContentDefinition;
  const webPushDataBody = webPushData.message ?? '';
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const { hasError: hasWebPushBodyError, hasWarning: hasWebPushBodyWarning } = constructFieldErrors(
    {
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.MESSAGE,
    },
  );
  const webPushBodyErrorText = constructErrorInlineText({
    hasError: hasWebPushBodyError,
    hasWarning: hasWebPushBodyWarning,
    condition: isEditMode,
  });

  const restrictionTextLabel = (
    <WebPushRestrictionText
      textContent={webPushDataBody}
      messageLength={webPushDataBody.length}
      contentType={ContentType.BODY}
    />
  );

  return (
    <FormField
      data-test="web-push-body-field"
      isHorizontalLayout
      labelText={displayContent.labelText}
      restrictionText={restrictionTextLabel}
      hasError={hasWebPushBodyError}
      hasWarning={hasWebPushBodyWarning}
      errorText={webPushBodyErrorText}
      warningText={webPushBodyErrorText}
    >
      <TextAreaField
        hasError={hasWebPushBodyError}
        hasWarning={hasWebPushBodyWarning}
        mediumRef={bodyRef}
        mediumDataKey={WEB_PUSH_BODY}
        mediumDataValue={webPushData.message}
        onChangeHandler={(content: string) => setCurrentMediumField(WEB_PUSH_BODY, content)}
      />
    </FormField>
  );
};
